// The complete set of (ordering, delivery, status) stack permutations that
// are actually live in production today — one preset per distinct
// order_status_configurations.stack_signature found via a grouped query
// against the nucleus replica (see nexus's OrderStatus::Configuration and
// LiveUpdates::StackPresenter for the signature vocabulary this mirrors).
// Deliberately NOT the full cross product of every ordering x every delivery
// provider: Olo only ever reports self_managed-vs-Olo-Dispatch (never a
// third-party courier identity), so "Olo ordering + DoorDash delivery" is not
// a real stack and has no preset here. Re-run the query below periodically
// (a new integration or provider combination going live won't retroactively
// appear in this list) to catch any newly-live permutation:
//
//   SELECT stack_signature, COUNT(DISTINCT merchant_id) merchants,
//          COUNT(DISTINCT location_id) locations
//   FROM order_status_configurations c
//   LEFT JOIN order_status_location_profiles lp ON lp.configuration_id = c.id
//   GROUP BY stack_signature ORDER BY locations DESC
//
// locationsCount below is that query's real (masked) location count per
// signature, snapshotted 2026-09, purely for label realism.
import type {
  LiveUpdatesConfigurationDetailWire,
  LiveUpdatesConfigurationsWire,
} from 'api/liveUpdates.types'
import { isOloProvider } from '../components/trigger'
import { buildStatusesWire } from '../fixtures'

type ConfigurationRowWire =
  LiveUpdatesConfigurationsWire['configurations'][number]

export type StackPreset = {
  id: string
  label: string
  row: ConfigurationRowWire
  detail: LiveUpdatesConfigurationDetailWire
  isOloOrdering: boolean
}

type PresetSeed = {
  id: string
  label: string
  orderingProvider: { key: string; display_name: string } | null
  deliveryProvider: { key: string; display_name: string } | null
  statusProvider: { key: string; display_name: string } | null
  locationsCount: number
  configurable: boolean
  deliveryConfigurable: boolean
  triggerLabel: string
  canceledByDeliveryTriggerLabel?: string
  pushManagedByFlybuy?: boolean
}

const REFRESHED_AT = '2026-09-01T12:00:00Z'

// Derived directly from nexus's LiveUpdates::StackPresenter (configurable?,
// delivery_configurable?, and the ordering/delivery display-name maps) rather
// than re-guessed — see that class for the source of truth this mirrors.
const SEEDS: PresetSeed[] = [
  {
    id: 'dev-olo-olo',
    label: 'Olo + Olo Dispatch',
    orderingProvider: { key: 'olo', display_name: 'Olo' },
    deliveryProvider: { key: 'olo', display_name: 'Olo Dispatch' },
    statusProvider: null,
    locationsCount: 3453,
    configurable: true,
    deliveryConfigurable: true,
    triggerLabel: 'Olo',
    canceledByDeliveryTriggerLabel: 'Olo Dispatch',
  },
  {
    id: 'dev-toast-uber',
    label: 'Toast + Uber',
    orderingProvider: { key: 'toast', display_name: 'Toast' },
    deliveryProvider: { key: 'uber', display_name: 'Uber' },
    statusProvider: null,
    locationsCount: 1031,
    configurable: true,
    deliveryConfigurable: true,
    triggerLabel: 'Toast',
    canceledByDeliveryTriggerLabel: 'Uber',
  },
  {
    id: 'dev-none-none',
    label: 'Not configured',
    orderingProvider: null,
    deliveryProvider: null,
    statusProvider: null,
    locationsCount: 752,
    configurable: false,
    deliveryConfigurable: false,
    triggerLabel: 'Not configured',
  },
  {
    id: 'dev-olo-disabled',
    label: 'Olo + Delivery Disabled',
    orderingProvider: { key: 'olo', display_name: 'Olo' },
    deliveryProvider: { key: 'disabled', display_name: 'Disabled' },
    statusProvider: null,
    locationsCount: 461,
    configurable: true,
    deliveryConfigurable: false,
    triggerLabel: 'Olo',
  },
  {
    id: 'dev-toast-disabled',
    label: 'Toast + Delivery Disabled',
    orderingProvider: { key: 'toast', display_name: 'Toast' },
    deliveryProvider: { key: 'disabled', display_name: 'Disabled' },
    statusProvider: null,
    locationsCount: 395,
    configurable: true,
    deliveryConfigurable: false,
    triggerLabel: 'Toast',
  },
  {
    id: 'dev-toast-doordash',
    label: 'Toast + DoorDash',
    orderingProvider: { key: 'toast', display_name: 'Toast' },
    deliveryProvider: { key: 'doordash', display_name: 'DoorDash' },
    statusProvider: null,
    locationsCount: 237,
    configurable: true,
    deliveryConfigurable: true,
    triggerLabel: 'Toast',
    canceledByDeliveryTriggerLabel: 'DoorDash',
  },
  {
    id: 'dev-unsupported-disabled',
    label: 'Unsupported + Delivery Disabled',
    orderingProvider: { key: 'unsupported', display_name: 'Unsupported' },
    deliveryProvider: { key: 'disabled', display_name: 'Disabled' },
    statusProvider: null,
    // Real ordering integration (Onosys/Square/QU) normalized to 'unsupported'
    // — see OrderStatus::LocationProfile::ORDERING_PROVIDERS.
    locationsCount: 74,
    configurable: false,
    deliveryConfigurable: false,
    triggerLabel: 'Unsupported',
  },
  {
    id: 'dev-toast-self-managed',
    label: 'Toast + Delivery Self-managed',
    orderingProvider: { key: 'toast', display_name: 'Toast' },
    deliveryProvider: { key: 'self_managed', display_name: 'Self-managed' },
    statusProvider: null,
    locationsCount: 71,
    configurable: true,
    deliveryConfigurable: false,
    triggerLabel: 'Toast',
  },
  {
    id: 'dev-deliverect-disabled',
    label: 'Deliverect + Delivery Disabled',
    orderingProvider: { key: 'deliverect', display_name: 'Deliverect' },
    deliveryProvider: { key: 'disabled', display_name: 'Disabled' },
    statusProvider: null,
    locationsCount: 18,
    configurable: true,
    deliveryConfigurable: false,
    triggerLabel: 'Deliverect',
  },
  {
    id: 'dev-flybuy',
    label: 'Flybuy',
    orderingProvider: null,
    deliveryProvider: null,
    statusProvider: { key: 'flybuy', display_name: 'Flybuy' },
    locationsCount: 18,
    configurable: true,
    // Flybuy is always delivery-configurable regardless of the underlying
    // stack (StackPresenter#delivery_configurable? short-circuits true for
    // it) — a merchant can pre-configure Delivery messaging ahead of need.
    deliveryConfigurable: true,
    triggerLabel: 'Flybuy',
    pushManagedByFlybuy: true,
  },
  {
    id: 'dev-olo-self-managed',
    label: 'Olo + Delivery Self-managed',
    orderingProvider: { key: 'olo', display_name: 'Olo' },
    deliveryProvider: { key: 'self_managed', display_name: 'Self-managed' },
    statusProvider: null,
    locationsCount: 8,
    configurable: true,
    deliveryConfigurable: false,
    triggerLabel: 'Olo',
  },
  {
    id: 'dev-deliverect-deliverect',
    label: 'Deliverect + Deliverect Dispatch',
    orderingProvider: { key: 'deliverect', display_name: 'Deliverect' },
    deliveryProvider: {
      key: 'deliverect',
      display_name: 'Deliverect Dispatch',
    },
    statusProvider: null,
    locationsCount: 5,
    configurable: true,
    deliveryConfigurable: true,
    triggerLabel: 'Deliverect',
    canceledByDeliveryTriggerLabel: 'Deliverect Dispatch',
  },
]

const buildPreset = (seed: PresetSeed): StackPreset => {
  const row: ConfigurationRowWire = {
    id: seed.id,
    label: seed.label,
    stack: {
      ordering_provider: seed.orderingProvider,
      delivery_provider: seed.deliveryProvider,
      status_provider: seed.statusProvider,
    },
    locations_count: seed.locationsCount,
    configurable: seed.configurable,
    delivery_configurable: seed.deliveryConfigurable,
    customized: false,
    last_published_at: null,
    refreshed_at: REFRESHED_AT,
  }

  const detail: LiveUpdatesConfigurationDetailWire = {
    configuration: {
      ...row,
      current_version: 0,
      statuses: buildStatusesWire({
        configurable: seed.configurable,
        deliveryConfigurable: seed.deliveryConfigurable,
        pushManagedByFlybuy: seed.pushManagedByFlybuy,
        triggerLabel: `Triggered by ${seed.triggerLabel}`,
        canceledByDeliveryTriggerLabel: seed.canceledByDeliveryTriggerLabel
          ? `Triggered by ${seed.canceledByDeliveryTriggerLabel}`
          : undefined,
        // `preparing` is the one seed that ships disabled, so without this
        // every preset would render it switched off and the Overview rail
        // would grey it on every stack — which reads as a bug rather than as
        // one configuration's choice. The toolbar is for stepping through
        // stacks, so each starts from a fully-enabled ladder; switch a stage
        // off on the Configuration tab to see the disabled treatment.
        overrides: { preparing: { enabled: true } },
      }),
    },
  }

  return {
    id: seed.id,
    label: seed.label,
    row,
    detail,
    isOloOrdering: isOloProvider(
      seed.orderingProvider && {
        key: seed.orderingProvider.key,
        displayName: seed.orderingProvider.display_name,
      }
    ),
  }
}

// Every live stack permutation, olo-ordering ones first purely so the most
// interesting (webhook-gated) presets sort to the top of the toolbar picker.
export const STACK_PRESETS: StackPreset[] = SEEDS.map(buildPreset)

export const DEFAULT_STACK_PRESET_ID = STACK_PRESETS[0].id
